import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    Alert,
    Badge,
    Button,
    Card,
    Col,
    Container,
    ListGroup,
    Row,
    Spinner,
} from 'react-bootstrap';

import SEO from '@/components/common/SEO';
import { useEcosystemApi } from '@/shared/services/ecosystemApi';
import { getEcosystemDestinationUrl } from '@/shared/utils/ecosystemRouting';
import {
    getPendingInvite,
    clearPendingInvite,
} from '@/features/invite/pendingInvite';
import './EcosystemChooser.css';

/**
 * EcosystemChooser: the post-login ecosystem hand-off page.
 *
 * This is the single chokepoint between authentication and tenant-context-aware
 * pages. Every post-auth entry point (LogIn, CreateProfile,
 * OrganizationOnboarding, the ProtectedRoutes guard for tenant-scoped routes
 * hit at apex) routes here, and this component branches on the user's
 * `EcosystemMembership` rows:
 *
 *   - 0 memberships → "Welcome to RILE Connect" with a CTA to browse
 *     hosted ecosystems at /ecosystems. The user has no tenant context to
 *     hand off to yet.
 *   - 1 membership → auto-redirect (full reload) to that ecosystem's
 *     `/dashboard`. The user has exactly one tenant context, so we skip the
 *     chooser UI entirely.
 *   - 2+ memberships → render the chooser list. The user picks an ecosystem
 *     and we full-reload to that tenant's `/dashboard`.
 *
 * Cross-tenant navigation MUST be a full reload (`window.location.href`),
 * NEVER a React Router navigate(). EcosystemProvider boots once at the top of
 * the React tree with `useEffect(..., [])`; a SPA push to `/{slug}` would not
 * re-bootstrap it, the BrowserRouter basename would stay '', and route
 * resolution would land on the catch-all 404. The same lesson the
 * EcosystemDirectory "Visit" button learned in v1.0.1 #56.
 *
 * The chooser uses `getEcosystemDestinationUrl` to compute the canonical host
 * for each ecosystem: apex (relative `/{slug}`), tenant subdomain (absolute
 * URL on apex), or custom apex domain, so the same code works in path mode
 * and (future) subdomain mode without branching.
 */
export default function EcosystemChooser() {
    const ecosystemApi = useEcosystemApi();

    const [memberships, setMemberships] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // True while completing a pending invite join (from an invite-link signup).
    const [joiningInvite, setJoiningInvite] = useState(() =>
        Boolean(getPendingInvite())
    );

    // Invite-link handoff (#171): a recipient who signed up from the invite
    // banner arrives here right after authenticating. Complete the join to that
    // ecosystem and route straight into it, bypassing the chooser.
    useEffect(() => {
        const slug = getPendingInvite();
        if (!slug) return undefined;
        let cancelled = false;
        (async () => {
            try {
                await ecosystemApi.joinEcosystem(slug);
                clearPendingInvite();
                if (cancelled) return;
                // Full reload so EcosystemProvider re-bootstraps under the tenant.
                window.location.href =
                    getEcosystemDestinationUrl({ slug }) + '/dashboard';
            } catch (err) {
                // Fall back to the normal chooser rather than trapping them.
                console.error('Auto-join from invite failed:', err);
                clearPendingInvite();
                if (!cancelled) setJoiningInvite(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [ecosystemApi]);

    const loadMemberships = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await ecosystemApi.fetchMyMemberships();
            setMemberships(data.memberships || []);
        } catch (err) {
            console.error(err);
            setError('Failed to load your ecosystems. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [ecosystemApi]);

    useEffect(() => {
        // Skip while completing a pending invite join — that effect redirects
        // away, so loading memberships here would be wasted work / a flash.
        if (joiningInvite) return undefined;
        loadMemberships();
        return undefined;
    }, [loadMemberships, joiningInvite]);

    // Auto-redirect when the user has exactly one membership: the chooser
    // has no decision to surface in that case. Runs in a separate effect (not
    // inside loadMemberships) so the redirect fires whenever the resolved
    // membership count is 1, including after a retry.
    useEffect(() => {
        if (loading || !memberships || memberships.length !== 1) return;
        const onlyOne = memberships[0];
        if (!onlyOne?.ecosystem?.slug) return;
        const destination =
            getEcosystemDestinationUrl(onlyOne.ecosystem) + '/dashboard';
        // Full reload so EcosystemProvider re-bootstraps under the new
        // tenant. NEVER use navigate() for cross-tenant navigation.
        window.location.href = destination;
    }, [loading, memberships]);

    const handlePick = (ecosystem) => {
        if (!ecosystem?.slug) return;
        const destination =
            getEcosystemDestinationUrl(ecosystem) + '/dashboard';
        window.location.href = destination;
    };

    // --- Loading ---
    if (loading) {
        return (
            <>
                <SEO title="Choose an Ecosystem" noindex />
                <Container className="py-5">
                    <Row className="justify-content-center">
                        <Col md={8} lg={6}>
                            <Card className="shadow-sm chooser-card">
                                <Card.Body className="p-4 text-center">
                                    <Spinner
                                        animation="border"
                                        role="status"
                                        aria-label="Loading your ecosystems"
                                    />
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </>
        );
    }

    // --- Error ---
    if (error) {
        return (
            <>
                <SEO title="Choose an Ecosystem" noindex />
                <Container className="py-5">
                    <Row className="justify-content-center">
                        <Col md={8} lg={6}>
                            <Card className="shadow-sm chooser-card">
                                <Card.Body className="p-4">
                                    <Alert variant="danger">{error}</Alert>
                                    <div className="d-grid">
                                        <Button
                                            variant="primary"
                                            onClick={loadMemberships}
                                        >
                                            Try Again
                                        </Button>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </>
        );
    }

    const count = memberships ? memberships.length : 0;

    // --- 0 memberships: welcome ---
    if (count === 0) {
        return (
            <>
                <SEO title="Welcome to RILE Connect" noindex />
                <Container className="py-5">
                    <Row className="justify-content-center">
                        <Col md={8} lg={6}>
                            <Card className="shadow-sm chooser-card">
                                <Card.Body className="p-4 text-center">
                                    <h2 className="text-primary mb-3">
                                        Welcome to RILE Connect
                                    </h2>
                                    <p className="text-muted mb-4">
                                        RILE Connect hosts a growing network of
                                        ecosystems. Pick one to join and get
                                        started.
                                    </p>
                                    <div className="d-grid gap-2">
                                        <Button
                                            as={Link}
                                            to="/ecosystems"
                                            variant="primary"
                                            className="rounded-pill"
                                        >
                                            Browse Ecosystems
                                        </Button>
                                        <Button
                                            as={Link}
                                            to="/profile"
                                            variant="link"
                                            className="text-muted"
                                        >
                                            Go to my profile instead
                                        </Button>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </>
        );
    }

    // --- 1 membership: auto-redirecting ---
    // The useEffect above has fired window.location.href = destination. While
    // the browser navigates we render a calm "Redirecting..." state rather
    // than the chooser list (which would flash for a frame).
    if (count === 1) {
        return (
            <>
                <SEO title="Redirecting…" noindex />
                <Container className="py-5">
                    <Row className="justify-content-center">
                        <Col md={8} lg={6}>
                            <Card className="shadow-sm chooser-card">
                                <Card.Body className="p-4 text-center">
                                    <Spinner
                                        animation="border"
                                        role="status"
                                        aria-label="Redirecting"
                                        className="mb-3"
                                    />
                                    <p className="text-muted mb-0">
                                        Taking you to{' '}
                                        <strong>
                                            {memberships[0]?.ecosystem?.name ||
                                                'your ecosystem'}
                                        </strong>
                                        …
                                    </p>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </>
        );
    }

    // --- N memberships: chooser ---
    return (
        <>
            <SEO title="Choose an Ecosystem" noindex />
            <Container className="py-5">
                <Row className="justify-content-center">
                    <Col md={8} lg={6}>
                        <Card className="shadow-sm chooser-card">
                            <Card.Body className="p-4">
                                <h2 className="text-center text-primary mb-2">
                                    Choose an Ecosystem
                                </h2>
                                <p className="text-center text-muted mb-4">
                                    You belong to more than one ecosystem. Which
                                    one would you like to open?
                                </p>
                                <ListGroup variant="flush">
                                    {memberships.map((m) => (
                                        <ListGroup.Item
                                            key={m.ecosystem.slug}
                                            action
                                            onClick={() =>
                                                handlePick(m.ecosystem)
                                            }
                                            className="d-flex justify-content-between align-items-center gap-3 py-3"
                                            data-testid={`chooser-row-${m.ecosystem.slug}`}
                                        >
                                            <div>
                                                <strong>
                                                    {m.ecosystem.name}
                                                </strong>
                                                <br />
                                                <small className="text-muted">
                                                    Joined{' '}
                                                    {m.joinedAt
                                                        ? new Date(
                                                              m.joinedAt
                                                          ).toLocaleDateString()
                                                        : 'unknown date'}
                                                </small>
                                            </div>
                                            <Badge
                                                bg="light"
                                                text="dark"
                                                className="chooser-role border"
                                            >
                                                {m.role}
                                            </Badge>
                                        </ListGroup.Item>
                                    ))}
                                </ListGroup>
                                <div className="text-center mt-4">
                                    <Link
                                        to="/ecosystems"
                                        className="text-muted small"
                                    >
                                        Browse all ecosystems
                                    </Link>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </>
    );
}
